"use client";
import { useEffect, useState, PropsWithChildren } from "react";
import "../css/globals.css";
import { getUtmsParams, retryOnce } from "@/utilities/helpers";
import { Analytics, initRemoteEventVersions } from "@/utilities/analytics";
import { LoginJourneyEnum } from "@/types/Analytics";
import Clarity from '@microsoft/clarity';
import { InscalarSDK } from "@ilg-core/inscalar-web";
import { eventVersionMap, versionsReady } from "@/utilities/analytics/event-version-map";
import datadog from "@ilg-core/analytics/datadog";
import { useSendSuperProperties } from "@/services/user";
import { DecodedUrlToken } from "@/types/User";
import { jwtDecode } from "jwt-decode";
import { env } from "next-runtime-env";

const UTM_PARAM_KEYS = [
  'af_ad', 'af_adset', 'af_channel', 'af_dp', 'af_status',
  'campaign', 'deep_link_value', 'media_source',
  'utm_campaign', 'utm_campaign_id', 'utm_content', 'utm_medium', 'utm_source', 'utm_term',
  'from_utm_campaign', 'from_utm_campaign_id', 'from_utm_content', 'from_utm_medium', 'from_utm_source', 'from_utm_term',
] as const;
  
const Providers = (props: PropsWithChildren<{}>) => {
  const [subjectId, setSubjectId] = useState<string>();
  const [isTrackingInitialised, setIsTrackingInitialised] =
    useState<boolean>(false);
  const [inscalarPropertiesSent, setInscalarPropertiesSent] = useState<boolean>(false);

  const [userAccessToken, setUserAccessToken] = useState<string>();
  const { trigger: sendSuperProperties } = useSendSuperProperties(userAccessToken);

  const collectUtmParams = () => {
    // Only run in browser environment
    if (typeof window === "undefined") return;

    // get all URL params and save to session storage
    // these are used within cross-domain journeys to save tracking parameters.
    let params = [];
    if (window.location.search) {
      //collecting all the url params in a list and replace body in aac/user/name api endpoint
      params = window.location.search
        .replace("?", "")
        .split("&")
        .map((item: string) => {
          const param: string[] = item.split("=");
          return {
            key: param[0],
            value: param[1],
          };
        })
        .filter(
          (item: { key: string; value: string }) => item.key !== "token"
        ) as [{ key: string; value: string }];
      window.sessionStorage.setItem(
        "urlParams",
        JSON.stringify(params) ?? ""
      );
    }
  }

  useEffect(() => {
    if (!userAccessToken) {
      fetch("/api/auth/session").then((session) => {
        session.json().then((data) => {
          if (!data.accessToken) return;
          const decodedUrlToken: Partial<DecodedUrlToken> = jwtDecode<DecodedUrlToken>(data.accessToken)
          setSubjectId(decodedUrlToken?.subjectId ?? '');
          if (data.accessToken) {
            setUserAccessToken(data.accessToken);
            initRemoteEventVersions();
            sessionStorage.setItem('token', data.accessToken)
          }
          if (decodedUrlToken.subjectId) {
            setSubjectId(decodedUrlToken.subjectId);
            sessionStorage.setItem('subjectId', decodedUrlToken.subjectId);
          }

          if (decodedUrlToken.subjectGuid) {
            sessionStorage.setItem('subjectGuid', decodedUrlToken.subjectGuid);
          }

          if (data.emailAddress) {
            sessionStorage.setItem('emailAddress', data.emailAddress);
          }
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && !!userAccessToken) {
      // initiate connection with Clarity
      const clarityProjectId = env("NEXT_PUBLIC_CLARITY_PROJECT_ID");
      if (clarityProjectId) {
        try {
          Clarity.init(clarityProjectId);
        } catch (error: any) {
          datadog.log.warn('Clarity initialization failed', { error })
        }
      } else {
        datadog.log.warn('Clarity project id is not configured, skipping initialization')
      }

      if (!subjectId) {
        datadog.log.warn('SubjectId is not available yet, analytics might be delayed');
        return;
      }

      // Wait for the remote event versions before firing version-sensitive
      // identity/consent events, so they don't use the stale bundled versions.
      void versionsReady.then(() => {
        try {
          // Identity enrichment — SDK already initialized by InscalarProvider on mount
          const storedEmail = sessionStorage.getItem('emailAddress');

          InscalarSDK.setUserProperties({
            customer_identifier: sessionStorage.getItem('subjectGuid') ?? '',
            ...(storedEmail ? { email: storedEmail } : {}),
          })
          InscalarSDK.setConsent(true, eventVersionMap['consent_updated'] ?? '1.1');

          InscalarSDK.identify(subjectId, {
            userIdentifiedEventVersion: eventVersionMap['user_identified'] ?? '1.0',
            profileAliasEventVersion: eventVersionMap['profile_alias'] ?? '1.0'
          })
            .then(() => {
              InscalarSDK.updateUserProfile(eventVersionMap['profile_updated'] ?? '1.0')
            }).catch((error: any) => {
              datadog.log.warn('[Inscalar] Identity enrichment failed', { error })
            })
        } catch (error: any) {
          datadog.log.warn('Inscalar initialization failed', { subjectId, error, stack: error?.stack })
        }
      })

      setIsTrackingInitialised(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subjectId]);

  useEffect(() => {
    if (typeof window !== "undefined" && userAccessToken) {
      collectUtmParams();
    }
  }, [userAccessToken]);

  const sendProperties = async () => {
    // Read UTM params from sessionStorage (persisted by collectUtmParams)
    let params: { key: string; value: string }[] = [];
    if (typeof window !== "undefined" && window.sessionStorage.getItem("urlParams")) {
      params = JSON.parse(window.sessionStorage.getItem("urlParams") ?? "[]");
    }
    const userProperties: { properties: { key: string, value: string }[] } = {
      properties: (UTM_PARAM_KEYS as readonly string[])
        .map(key => {
          const found = params.find(param => param.key === key);
          return found ? { key, value: found.value } : null;
        })
        .filter((entry): entry is { key: string, value: string } => entry !== null && entry.value.trim() !== ""),
    };

    if (userProperties.properties.length === 0) {
      datadog.log.info('No super properties to send', { userProperties });
      return;
    }

    try {
      await retryOnce(() => sendSuperProperties(userProperties));
      datadog.log.info('Super properties sent successfully', { userProperties });
      setInscalarPropertiesSent(true);
    } catch (error: any) {
      datadog.log.error('Failed to send super properties after retry', {
        error,
        userProperties,
        statusCode: error?.status,
        statusMessage: error?.statusText ?? error?.message,
        cause: error instanceof Error ? error.cause : undefined,
      });
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined" && isTrackingInitialised) {
      const utms = getUtmsParams();
      InscalarSDK.setUtmParameters(utms, !inscalarPropertiesSent ? sendProperties : undefined);

      Analytics.logEvent("logged_in", {
        login_journey:
          utms.utmSource === "crm"
            ? LoginJourneyEnum.CRM
            : LoginJourneyEnum.STANDARD,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrackingInitialised]);

  return props.children;
};

export default Providers;
